import React, { useEffect, useState } from "react";
import axios from "axios";
import { CheckCircle2, AlertTriangle, XCircle, Info, Share2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VERDICT_META = {
  PROCEED: { bg: "bg-[#002FA7]", text: "text-white", icon: CheckCircle2, label: "PROCEED" },
  CAUTION: { bg: "bg-[#FFD700]", text: "text-[#0a0a0a]", icon: AlertTriangle, label: "CAUTION" },
  AVOID: { bg: "bg-[#FF2400]", text: "text-white", icon: XCircle, label: "AVOID" },
  INSUFFICIENT_DATA: { bg: "bg-[#666666]", text: "text-white", icon: Info, label: "INSUFF DATA" },
};

const formatDate = (ts) => {
  if (!ts) return "—";
  try {
    return new Date(ts).toLocaleDateString("id-ID", { day: "numeric", month: "short" });
  } catch (e) {
    return "—";
  }
};

export default function RecentAudits() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/audits/recent?limit=9`, { timeout: 12000 });
        if (!cancelled) setItems(res.data || []);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!loading && error) return null;

  return (
    <section
      id="recent"
      className="border-b-2 border-black bg-[#fdfdfd]"
      data-testid="recent-audits-section"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-20 py-16 md:py-24">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#666666]">
              / 05 — Live Audit Feed
            </div>
            <h2 className="font-display font-black uppercase tracking-tighter text-4xl md:text-5xl mt-3 leading-[0.95]">
              Audit
              <br />
              Terbaru.
            </h2>
          </div>
        </div>

        {/* DYNAMIC DISCLAIMER */}
        <div
          className="mt-6 border-2 border-black bg-[#fff8d6] px-4 py-3 flex items-start gap-3"
          data-testid="recent-dynamic-disclaimer"
        >
          <Info className="w-4 h-4 mt-0.5 flex-shrink-0" strokeWidth={2.5} />
          <p className="font-sans text-xs md:text-sm leading-snug text-[#0a0a0a]">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] font-bold mr-1">CATATAN:</span>
            Setiap hasil audit bersifat <span className="font-bold">dinamis</span>. Skor dan hasil akhir (verdict) sangat bergantung pada waktu pelaksanaan audit. Ulasan terbaru di YouTube, TikTok, Shopee, Tokopedia, atau berbagai forum akan memengaruhi hasil audit berikutnya. Jadikan hasil ini sebagai <span className="font-bold">snapshot</span> (gambaran sesaat) pada waktu tertentu, bukan sebagai kebenaran absolut. <span className="font-bold">Mohon lakukan audit secara berkala untuk mendapatkan data terbaru.</span>
          </p>
        </div>

        {loading && (
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-0 border-2 border-black">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div
                key={i}
                className={`p-6 ${i % 3 !== 2 ? "lg:border-r-2 lg:border-black" : ""} ${i < 3 ? "border-b-2 border-black" : ""} ${i % 2 === 0 && i < 4 ? "sm:border-r-2 sm:border-black" : ""}`}
              >
                <div className="h-3 w-24 bg-[#eaeaea]" />
                <div className="h-6 w-3/4 bg-[#eaeaea] mt-3" />
                <div className="h-3 w-full bg-[#f3f3f3] mt-3" />
                <div className="h-3 w-2/3 bg-[#f3f3f3] mt-2" />
              </div>
            ))}
          </div>
        )}

        {!loading && items.length > 0 && (
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-0 border-2 border-black">
            {items.slice(0, 9).map((it, i) => (
              <RecentCard key={it.id || i} item={it} index={i} total={Math.min(items.length, 9)} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function RecentCard({ item, index, total }) {
  const meta = VERDICT_META[item.verdict] || VERDICT_META.INSUFFICIENT_DATA;
  const Icon = meta.icon;
  const isLastCol = (index + 1) % 3 === 0;
  const lastRowStart = Math.floor((total - 1) / 3) * 3;
  const isLastRow = index >= lastRowStart;

  const shareToWhatsApp = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const url = `${window.location.origin}/a/${item.id}`;
    const scoreLine = typeof item.overall_score === "number"
      ? `🎯 Score: ${item.overall_score}/100`
      : "";
    const lines = [
      `[VERDICT: ${meta.label}] ${item.brand} ${item.model}${item.category ? ` (${item.category})` : ""}`,
      scoreLine,
      "",
      item.reality_check ? `📊 ${item.reality_check}` : "",
      "",
      `🔍 Audit lengkap: ${url}`,
      `Diaudit oleh FAKTA PRODUK — Chief Auditor untuk konsumen Indonesia.`,
    ].filter(Boolean);
    const text = encodeURIComponent(lines.join("\n"));
    window.open(`https://wa.me/?text=${text}`, "_blank", "noopener");
  };

  return (
    <article
      className={`relative p-6 hover-brutal bg-white ${!isLastCol ? "lg:border-r-2 lg:border-black" : ""} ${index % 2 === 0 ? "sm:border-r-2 sm:border-black" : ""} ${!isLastRow ? "border-b-2 border-black" : ""}`}
      data-testid={`recent-audit-${index}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className={`inline-flex items-center gap-1.5 px-2 py-1 border-2 border-black ${meta.bg} ${meta.text}`}>
          <Icon className="w-3 h-3" strokeWidth={2.5} />
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] font-bold">
            {meta.label}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {typeof item.overall_score === "number" && (
            <div
              className="border-2 border-black bg-[#0a0a0a] text-white px-2 py-1 font-mono text-[10px] uppercase tracking-[0.18em] font-bold leading-none"
              data-testid={`recent-score-${index}`}
              title="Overall Audit Score (0-100)"
            >
              <span className="text-[#FFD700]">{item.overall_score}</span>
              <span className="opacity-50">/100</span>
            </div>
          )}
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#888]">
            {formatDate(item.timestamp)}
          </div>
        </div>
      </div>

      <h3 className="mt-3 font-display font-black uppercase tracking-tight text-xl md:text-2xl leading-tight">
        {item.brand} {item.model}
      </h3>
      {item.category && (
        <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-[#666]">
          {item.category}
        </div>
      )}

      <p className="mt-3 font-sans text-sm leading-snug text-[#333] line-clamp-3">
        {item.reality_check || "—"}
      </p>

      <div className="mt-4 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.18em] text-[#888]">
          <span>{item.tier === "premium" ? "◆ Premium" : "⚡ Fast"}</span>
          {item.data_source === "apify_serp" && item.scraped_counts && (
            <span className="text-[#002FA7]">
              {Object.values(item.scraped_counts).reduce((a, b) => a + (b || 0), 0)} hits
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={shareToWhatsApp}
          className="border-2 border-black bg-[#25D366] text-black px-2 py-1 hover:bg-black hover:text-[#25D366] transition-colors inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.18em]"
          data-testid={`share-recent-${index}`}
          title="Bagikan ringkasan ke WhatsApp"
        >
          <Share2 className="w-3 h-3" /> Share
        </button>
      </div>
    </article>
  );
}
